import React from 'react';
import styled from 'styled-components';
import { Users, Grid3x3, Heart, MessageCircle, Activity, CalendarDays, Target } from 'lucide-react';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';
import { Card, Muted, ScoreBar, ScoreRing, Section } from '../parts';
import { formatDate, formatNumber } from '../labels';

export default function OverviewTab({ analysis }) {
    const { result, previous } = analysis;
    const metrics = result.metrics;
    const notes = metrics.notes || {};

    const metricCards = [
        { key: 'followers', icon: Users, label: 'Seguidores', value: formatNumber(metrics.followers) },
        { key: 'posts', icon: Grid3x3, label: 'Posts', value: formatNumber(metrics.posts) },
        {
            key: 'likes',
            icon: Heart,
            label: metrics.avg_likes !== null ? 'Curtidas por post' : 'Maior número de curtidas',
            value: formatNumber(metrics.avg_likes ?? metrics.peak_likes) ?? 'Ocultas',
        },
        { key: 'comments', icon: MessageCircle, label: 'Comentários por post', value: formatNumber(metrics.avg_comments) ?? 'Ocultos' },
        {
            key: 'engagement_rate',
            icon: Activity,
            label: 'Taxa de engajamento',
            value: metrics.engagement_rate !== null ? `${formatNumber(metrics.engagement_rate, { maximumFractionDigits: 2 })}%` : '—',
        },
        { key: 'frequency', icon: CalendarDays, label: 'Posts por semana', value: formatNumber(metrics.posts_per_week, { maximumFractionDigits: 1 }) },
    ];

    return (
        <>
            <Hero>
                <ScoreRing score={result.overall_score} label='nota geral' />
                <HeroText>
                    <Headline>{result.headline}</Headline>
                    <Muted>{result.summary}</Muted>
                    <Meta>
                        {metrics.posts_analyzed} posts analisados, de {formatDate(metrics.period_start)} a {formatDate(metrics.period_end)}
                        {previous && <> · comparado com a análise de {formatDate(previous.completed_at)}</>}
                    </Meta>
                </HeroText>
            </Hero>

            <Columns>
                <Section icon={Target} title='Notas por pilar' subtitle='De 0 a 100, pelos mesmos critérios todo mês'>
                    <Card>
                        <Bars>
                            {result.pillars.map(pillar => (
                                <ScoreBar
                                    key={pillar.key}
                                    title={pillar.title}
                                    score={pillar.score}
                                    previous={previous?.pillars?.[pillar.key]}
                                />
                            ))}
                        </Bars>
                    </Card>
                </Section>

                <Section icon={Target} title='Comece por aqui' subtitle='As três mudanças com mais retorno pelo menor esforço'>
                    <Priorities>
                        {result.plan.priority_actions.map((action, index) => (
                            <Priority key={index}>
                                <PriorityNumber>{index + 1}</PriorityNumber>
                                <div>
                                    <PriorityTitle>{action.title}</PriorityTitle>
                                    <Muted>{action.description}</Muted>
                                </div>
                            </Priority>
                        ))}
                    </Priorities>
                </Section>
            </Columns>

            <Section icon={Activity} title='Números do perfil'>
                <MetricsGrid>
                    {metricCards.map(metric => (
                        <Card key={metric.key}>
                            <MetricTop>
                                <MetricIcon><metric.icon size={16} color={panelAccent(colors.secondaryPurple)} /></MetricIcon>
                                <MetricLabel>{metric.label}</MetricLabel>
                            </MetricTop>
                            <MetricValue>{metric.value}</MetricValue>
                            {notes[metric.key] && <Muted>{notes[metric.key]}</Muted>}
                        </Card>
                    ))}
                </MetricsGrid>
            </Section>
        </>
    );
}

const MetricsGrid = styled.div`
    display: grid;
    gap: 14px;
    grid-template-columns: 1fr;

    @media screen and (min-width: ${breakpoints.sm}) {
        grid-template-columns: repeat(2, 1fr);
    }

    @media screen and (min-width: ${breakpoints.md}) {
        grid-template-columns: repeat(3, 1fr);
    }
`;

const Hero = styled(Card)`
    display: flex;
    gap: 24px;
    align-items: center;
    margin-bottom: 28px;

    @media screen and (max-width: ${breakpoints.sm}) {
        flex-direction: column;
        align-items: flex-start;
    }
`;

const HeroText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const Headline = styled.h2`
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    line-height: 1.35;
    color: ${colors.nightDark};
`;

const Meta = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
`;

const Columns = styled.div`
    display: grid;
    gap: 0 24px;
    grid-template-columns: 1fr 1fr;

    @media screen and (max-width: 1000px) {
        grid-template-columns: 1fr;
    }
`;

const Bars = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const Priorities = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const Priority = styled(Card)`
    display: flex;
    gap: 12px;
    align-items: flex-start;
    padding: 14px 16px;
`;

const PriorityNumber = styled.span`
    width: 26px;
    height: 26px;
    border-radius: 8px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 13px;
    font-weight: 700;
    background: ${panelAccentAlpha(colors.secondaryPurple, '14')};
    color: ${panelAccent(colors.secondaryPurple)};
`;

const PriorityTitle = styled.p`
    margin: 0 0 2px;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const MetricTop = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
`;

const MetricIcon = styled.div`
    width: 30px;
    height: 30px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${panelAccentAlpha(colors.occasionalPurple, '10')};
`;

const MetricLabel = styled.span`
    font-size: 13px;
    font-weight: 500;
    color: ${colors.night};
`;

const MetricValue = styled.div`
    font-size: 24px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin-bottom: 6px;
`;
